<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TicketCategoriesAPI from 'dashboard/api/ticketCategories';
import CeaLookupsAPI from 'dashboard/api/ceaLookups';
import Button from 'dashboard/components-next/button/Button.vue';
import WorkflowConfigEditor from './WorkflowConfigEditor.vue';
import DocumentRequestTemplatesEditor from './DocumentRequestTemplatesEditor.vue';

const props = defineProps({
  category: { type: Object, required: true },
  subcategory: { type: Object, default: null },
});

const emit = defineEmits(['saved', 'cancel']);

const { t } = useI18n();

const form = ref({
  name: '',
  code: '',
  description: '',
  default_priority: 2,
  sla_hours: null,
  active: true,
  requires_contract: false,
  requires_location: false,
  requires_human_escalation: false,
  workflow_config: {},
  subcategory_tipo_id: null,
});

const tipos = ref([]);

const showWorkflowConfig = ref(false);
const showDocumentTemplates = ref(false);

// Required-document checklist template, stored in workflow_config.document_requests.
const documentTemplates = computed({
  get: () => form.value.workflow_config?.document_requests || [],
  set: value => {
    form.value.workflow_config = {
      ...(form.value.workflow_config || {}),
      document_requests: value,
    };
  },
});

const workflowSectionSummary = computed(() => {
  const wf = form.value.workflow_config;
  const fields = wf?.fields;
  if (!Array.isArray(fields) || fields.length === 0) return '';
  const preview = fields
    .map(f => (f.label || f.key || '').trim())
    .filter(Boolean)
    .slice(0, 4)
    .join(', ');
  const tt = (wf.on_complete?.title_template || '').trim();
  const countLabel = t('WORKFLOW_CONFIG.WORKFLOW_FIELDS_COUNT', {
    count: fields.length,
  });
  const titleTplLabel = t('WORKFLOW_CONFIG.TITLE_TEMPLATE');
  const parts = [countLabel];
  if (preview) parts.push(preview);
  if (tt) parts.push(`${titleTplLabel}: ${tt}`);
  return parts.join(' · ');
});

const DEDUP_STRATEGY_KEYS = [
  'none',
  'contract',
  'contract_and_address',
  'address',
  'contract_and_explotacion',
];

const effectiveDedupStrategyKey = computed(() => {
  const wf = form.value.workflow_config || {};
  const explicit = wf.dedup?.strategy;
  if (explicit && DEDUP_STRATEGY_KEYS.includes(explicit)) {
    return explicit;
  }
  const rc = form.value.requires_contract;
  const rl = form.value.requires_location;
  if (rc && rl) return 'contract_and_address';
  if (rc && !rl) return 'contract';
  if (!rc && rl) return 'address';
  return 'none';
});

const effectiveDedupSummary = computed(() =>
  t(`WORKFLOW_CONFIG.DEDUP_SUMMARY.${effectiveDedupStrategyKey.value}`)
);

const saving = ref(false);
const isEditing = computed(() => !!props.subcategory?.id);

const priorities = [
  { value: 1, label: 'Baja', color: 'green' },
  { value: 2, label: 'Media', color: 'blue' },
  { value: 3, label: 'Alta', color: 'orange' },
  { value: 4, label: 'Urgente', color: 'red' },
];

const fetchTipos = async () => {
  try {
    const response = await CeaLookupsAPI.get('subcategory_tipos');
    tipos.value = response.data;
  } catch {
    // Non-blocking
  }
};

onMounted(() => {
  if (props.subcategory) {
    form.value = { ...props.subcategory };
    // Strip category prefix from code for display (it's shown separately)
    const prefix = `${props.category.code}-`;
    if (form.value.code?.startsWith(prefix)) {
      form.value.code = form.value.code.substring(prefix.length);
    }
    const wfFields = form.value.workflow_config?.fields;
    if (Array.isArray(wfFields) && wfFields.length > 0) {
      showWorkflowConfig.value = true;
    }
    const docTemplates = form.value.workflow_config?.document_requests;
    if (Array.isArray(docTemplates) && docTemplates.length > 0) {
      showDocumentTemplates.value = true;
    }
  }
  fetchTipos();
});

const handleSubmit = async () => {
  saving.value = true;

  try {
    // Ensure code has category prefix
    let { code } = form.value;
    if (!code.startsWith(`${props.category.code}-`)) {
      code = `${props.category.code}-${code}`;
    }

    const data = { ...form.value, code };

    if (isEditing.value) {
      await TicketCategoriesAPI.updateSubcategory(
        props.category.id,
        props.subcategory.id,
        data
      );
      useAlert(t('TICKET_SUBCATEGORIES.UPDATE_SUCCESS'));
    } else {
      await TicketCategoriesAPI.createSubcategory(props.category.id, data);
      useAlert(t('TICKET_SUBCATEGORIES.CREATE_SUCCESS'));
    }
    emit('saved');
  } catch (error) {
    const errors = error.response?.data?.errors || [
      t('TICKET_SUBCATEGORIES.SAVE_ERROR'),
    ];
    useAlert(errors.join(', '));
  } finally {
    saving.value = false;
  }
};

const autoGenerateCode = () => {
  if (!form.value.code && form.value.name) {
    // Only generate the suffix part (prefix is shown separately in UI)
    form.value.code = form.value.name
      .substring(0, 3)
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }
};
</script>

<template>
  <div class="flex flex-col w-full max-w-lg">
    <woot-modal-header
      :header-title="
        isEditing
          ? $t('TICKET_SUBCATEGORIES.EDIT')
          : $t('TICKET_SUBCATEGORIES.NEW')
      "
      :header-content="`${$t('TICKET_SUBCATEGORIES.CATEGORY')}: ${category.name}`"
    />

    <form class="flex flex-col gap-4 p-6" @submit.prevent="handleSubmit">
      <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
      <!-- Name -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('TICKET_SUBCATEGORIES.FORM.NAME') }} *
        </label>
        <input
          v-model="form.name"
          type="text"
          required
          maxlength="150"
          class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
          @blur="autoGenerateCode"
        />
      </div>

      <!-- Code -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('TICKET_SUBCATEGORIES.FORM.CODE') }} *
        </label>
        <div class="flex items-center gap-2">
          <span class="text-sm text-n-slate-11 font-mono">
            {{ category.code }}-
          </span>
          <input
            v-model="form.code"
            type="text"
            required
            maxlength="15"
            class="flex-1 px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak font-mono uppercase focus:border-woot-500 focus:outline-none"
            placeholder="XXX"
          />
        </div>
      </div>

      <!-- Description -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('TICKET_SUBCATEGORIES.FORM.DESCRIPTION') }}
        </label>
        <textarea
          v-model="form.description"
          rows="3"
          class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none text-sm"
          :placeholder="$t('TICKET_SUBCATEGORIES.FORM.DESCRIPTION_PLACEHOLDER')"
        />
        <span class="text-xs text-n-slate-10">
          {{ $t('TICKET_SUBCATEGORIES.FORM.DESCRIPTION_HELP') }}
        </span>
      </div>

      <!-- Tipo -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12"> Tipo </label>
        <select
          v-model="form.subcategory_tipo_id"
          class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
        >
          <option :value="null">Sin tipo</option>
          <option v-for="tipo in tipos" :key="tipo.id" :value="tipo.id">
            {{ tipo.description }}
          </option>
        </select>
        <span class="text-xs text-n-slate-10">
          Agrupa subcategorias para reportes
        </span>
      </div>

      <!-- Priority -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('TICKET_SUBCATEGORIES.FORM.PRIORITY') }}
        </label>
        <select
          v-model.number="form.default_priority"
          class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
        >
          <option v-for="p in priorities" :key="p.value" :value="p.value">
            {{ p.label }}
          </option>
        </select>
      </div>

      <!-- SLA -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('TICKET_SUBCATEGORIES.FORM.SLA_HOURS') }}
        </label>
        <input
          v-model.number="form.sla_hours"
          type="number"
          min="1"
          class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none"
          placeholder="24"
        />
      </div>

      <!-- Contract / location (drives default dedup strategy) -->
      <div class="flex flex-col gap-2">
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            v-model="form.requires_contract"
            type="checkbox"
            class="rounded"
          />
          <span class="text-sm text-n-slate-12">
            {{ $t('TICKET_SUBCATEGORIES.FORM.REQUIRES_CONTRACT') }}
          </span>
        </label>
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            v-model="form.requires_location"
            type="checkbox"
            class="rounded"
          />
          <span class="text-sm text-n-slate-12">
            {{ $t('TICKET_SUBCATEGORIES.FORM.REQUIRES_LOCATION') }}
          </span>
        </label>
        <p class="text-xs text-n-slate-10">
          {{
            $t('TICKET_SUBCATEGORIES.FORM.DEDUP_EFFECT', {
              summary: effectiveDedupSummary,
            })
          }}
        </p>
      </div>

      <!-- Checkboxes -->
      <div class="flex flex-col gap-2">
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            v-model="form.requires_human_escalation"
            type="checkbox"
            class="rounded"
          />
          <span class="text-sm text-n-slate-12">
            {{ $t('TICKET_SUBCATEGORIES.FORM.REQUIRES_HUMAN_ESCALATION') }}
          </span>
        </label>
        <label class="flex items-center gap-2 cursor-pointer">
          <input v-model="form.active" type="checkbox" class="rounded" />
          <span class="text-sm text-n-slate-12">
            {{ $t('TICKET_SUBCATEGORIES.FORM.ACTIVE') }}
          </span>
        </label>
      </div>

      <!-- Workflow Configuration (Collapsible) -->
      <div class="border border-n-weak rounded-lg overflow-hidden">
        <button
          type="button"
          class="w-full flex items-center justify-between gap-2 p-3 bg-n-solid-2 hover:bg-n-alpha-1 text-left"
          @click="showWorkflowConfig = !showWorkflowConfig"
        >
          <div class="flex flex-col gap-0.5 min-w-0 flex-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('WORKFLOW_CONFIG.SECTION_TITLE') }}
            </span>
            <span
              v-if="workflowSectionSummary && !showWorkflowConfig"
              class="text-xs text-n-slate-10 font-normal line-clamp-2"
            >
              {{ workflowSectionSummary }}
            </span>
          </div>
          <fluent-icon
            :icon="showWorkflowConfig ? 'chevron-up' : 'chevron-down'"
            size="16"
            class="text-n-slate-11 shrink-0"
          />
        </button>
        <div v-if="showWorkflowConfig" class="p-4 border-t border-n-weak">
          <WorkflowConfigEditor v-model="form.workflow_config" />
        </div>
      </div>

      <!-- Required Documents Template (Collapsible) -->
      <div class="border border-n-weak rounded-lg overflow-hidden">
        <button
          type="button"
          class="w-full flex items-center justify-between gap-2 p-3 bg-n-solid-2 hover:bg-n-alpha-1 text-left"
          @click="showDocumentTemplates = !showDocumentTemplates"
        >
          <div class="flex flex-col gap-0.5 min-w-0 flex-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('DOCUMENT_REQUEST_TEMPLATES.SECTION_TITLE') }}
            </span>
            <span
              v-if="documentTemplates.length && !showDocumentTemplates"
              class="text-xs text-n-slate-10 font-normal"
            >
              {{ $t('DOCUMENT_REQUEST_TEMPLATES.SUMMARY', { count: documentTemplates.length }) }}
            </span>
          </div>
          <fluent-icon
            :icon="showDocumentTemplates ? 'chevron-up' : 'chevron-down'"
            size="16"
            class="text-n-slate-11 shrink-0"
          />
        </button>
        <div v-if="showDocumentTemplates" class="p-4 border-t border-n-weak">
          <DocumentRequestTemplatesEditor v-model="documentTemplates" />
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-2 pt-4">
        <Button
          slate
          faded
          :label="$t('TICKET_SUBCATEGORIES.FORM.CANCEL')"
          @click="emit('cancel')"
        />
        <Button
          type="submit"
          :is-loading="saving"
          :label="
            isEditing
              ? $t('TICKET_SUBCATEGORIES.FORM.UPDATE')
              : $t('TICKET_SUBCATEGORIES.FORM.CREATE')
          "
        />
      </div>
      <!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
    </form>
  </div>
</template>
